/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { useDrawdownRequestContext } from './DrawdownRequestContext';
import { OrderSummary } from '../../../../components/OrderSummary';
import { colors } from '@farmlands/mui-theme';
import { Stack, styled } from '@mui/material';
import Typography from '../../../../components/Typography';
import TextField from '../../../../components/TextField';
import { ModalActions } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { useSelector } from 'react-redux';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { formatPrice } from '../../../../util/helpers';
import { useNavigate } from 'react-router-dom';
import ProductLine from './ProductLine';
import { EditButton } from '../../../products/components/ProductCard/EditButton';
import { submitDrawdown } from './api';
import { SubmitDrawdownPayload } from './types';
import dayjs from 'dayjs';
import { selectConfigByKey } from '../../../../services/firebase/remote-config/remoteConfig.slice';
import { Loader } from '../../../../components/Loader';
import { capitaliseFirstLetter, pluraliseUnit } from '../../../../util/consts';
import { addGST, getGST } from '../../../../util/addGst';

const Tile = styled(Stack)`
  background: ${colors.primary100};
  padding: 1rem;
  border-radius: 8px;
  // first h6 add 8px margin bottom
  h6 {
    margin-bottom: 8px;
  }
`;

const Step4 = () => {
  const { state, setState, salesAgreement } = useDrawdownRequestContext();
  const account = useSelector(selectCurrentFarmerAccount);

  const navigate = useNavigate();
  // Calculate total, delivery, and GST based on productRequests
  const totalNoGst = state.productRequest.reduce(
    (acc, product) => acc + product.unitPrice * product.requestQuantity,
    0,
  );

  const totalWithGst = addGST(totalNoGst);
  const delivery =
    state.drawdownConfig?.deliveryType === 'Delivery' && state.drawdownConfig.freightNote
      ? state.drawdownConfig.freightNote
      : undefined;
  const gst = getGST(totalNoGst);

  const setNotes = (notes: string) => {
    setState({ notes });
  };

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const drawdownFulfilmentEnabled: boolean = useSelector(
    selectConfigByKey('drawdownFulfilmentEnabled'),
  );

  const [loading, setLoading] = React.useState(false);

  if (!state.productRequest) return null;

  if (loading) {
    return (
      <Stack alignItems='center' justifyContent='center' height='100%'>
        <Loader title='Submitting order' />
      </Stack>
    );
  }

  return (
    <div>
      <Stack mb={2}>
        <Typography variant='h6'>Summary</Typography>
        <Typography variant='body2'>Review your products and confirm delivery</Typography>
      </Stack>
      <Stack gap={2}>
        {state.productRequest.map((product) => (
          <ProductLine
            hideRemainder
            month={dayjs(state.selectedMonth?.value).format('MMMM')}
            showAdditives={state.drawdownConfig?.showAdditives ?? !!product.feedRate}
            product={product}
            key={product.productVariantNumber}
            amounts={{
              [product.productVariantNumber]: {
                requestQuantity: product.requestQuantity.toString(),
                additives: product.additives,
                feedRate: product.feedRate,
              },
            }}
            disabled
            handleAmountChange={console.log}
          />
        ))}
        <Stack gap={2} p={2} bgcolor={colors.primary50} border={`1px solid ${colors.primary200}`}>
          <OrderSummary
            title='Order Summary'
            accountName={account?.accountName}
            accountNumber={account?.accountNumber}
            total={formatPrice(totalWithGst)}
            delivery={delivery ?? undefined}
            gst={formatPrice(gst)}
            showDivider
          />

          <Stack gap={1}>
            {state.drawdownConfig?.freightType && (
              <Tile>
                <Typography variant='h6'>Freight Details</Typography>
                <Typography variant='body2' color='primary700'>
                  {state.drawdownConfig.freightType}
                </Typography>
              </Tile>
            )}

            {state.userEdited?.pickupStoreSelected && (
              <Tile>
                <Stack direction='row' gap={1}>
                  <Typography variant='h6'>Pickup from store:</Typography>
                </Stack>
                <Typography variant='body2' color='primary700'>
                  Farmlands {state.userEdited?.pickupStoreSelected.orgUnitName}
                  <br />
                  {state.userEdited?.pickupStoreSelected.address}
                </Typography>
              </Tile>
            )}

            {state.userEdited?.addressSelected && (
              <Tile>
                <Stack direction='row' gap={1} justifyContent='space-between'>
                  <Stack>
                    <Stack direction='row' gap={1}>
                      <Typography variant='h6'>
                        <MaterialSymbol>local_shipping</MaterialSymbol>
                      </Typography>
                      <Typography variant='h6'>Delivery</Typography>
                    </Stack>
                    <Typography variant='body2' color='primary700'>
                      {state.userEdited?.addressSelected.addressName ? (
                        <Typography
                          component='span'
                          variant='body2'
                          color='primary700'
                          fontWeight='600'
                        >
                          {state.userEdited?.addressSelected.addressName}
                          <br />
                        </Typography>
                      ) : (
                        ''
                      )}
                      {state.userEdited?.addressSelected.address}
                      <br />
                      {state.drawdownConfig?.deliveryContactName}
                      <br />
                      {state.drawdownConfig?.deliveryContactPhone}

                      <br />
                      <br />
                      {state.userEdited.preferredDate?.format('DD MMM YYYY')}
                      <br />
                      {state.userEdited.deliveryInstructions}
                    </Typography>
                  </Stack>
                  {drawdownFulfilmentEnabled && (
                    <EditButton
                      onClick={() => {
                        setState({
                          tabIndex: 1,
                        });
                      }}
                    />
                  )}
                </Stack>
              </Tile>
            )}

            {state.userEdited?.dairySupplyNumber && (
              <Tile>
                <Typography variant='h6'>Dairy Supply Number</Typography>
                <Typography variant='body2' color='primary700'>
                  {state.userEdited?.dairySupplyNumber}
                </Typography>
              </Tile>
            )}

            {state.drawdownConfig?.carrier && (
              <Tile>
                <Typography variant='h6'>Carrier details</Typography>
                <Typography variant='body2' color='primary700'>
                  {state.drawdownConfig.truck} - {state.drawdownConfig.carrier}
                </Typography>
              </Tile>
            )}

            {/* Need to get split payment details from sales agreement */}
            {state.drawdownConfig?.splitPayment && (
              <Tile>
                <Typography variant='h6'>Split payment with</Typography>
                <Typography variant='body2' color='primary700'>
                  {state.drawdownConfig.splitPayment.accountNumber}
                  <br />
                  {state.drawdownConfig.splitPayment.accountName}
                  <br />
                  <br />
                  Their contribution: {state.drawdownConfig.splitPayment.percentage}%
                </Typography>
              </Tile>
            )}
          </Stack>
        </Stack>

        <TextField
          variant='outlined'
          maxRows={4}
          minRows={3}
          multiline
          value={state.notes}
          onChange={(event) => setNotes(event.target.value)}
          label='Add a note'
          inputProps={{ maxLength: 200 }}
          helperText='Add comments or questions regarding your requested delivery amount.'
          helperTextColor={colors.grey700}
        />
      </Stack>

      <ModalActions>
        {submitError && (
          <Typography variant='body2' color='error500' sx={{ mr: 'auto' }}>
            {submitError}
          </Typography>
        )}
        <Button
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
          variant='contained'
          type='submit'
          onClick={() => {
            const payload: SubmitDrawdownPayload = {
              note: state.notes,
              accountNumber: account!.accountNumber,
              preferredDate: state.userEdited?.preferredDate?.toISOString(),
              releaseDate: dayjs(state.selectedMonth?.value).startOf('month').toISOString(),
              pickupStoreId: state.userEdited?.pickupStoreSelected?.inventoryLocationId,
              delivery: state.userEdited?.addressSelected?.address
                ? {
                    deliveryAddress: {
                      recordId: state.userEdited?.addressSelected?.recordId,
                      name: state.userEdited?.addressSelected?.addressName,
                      streetAddress: state.userEdited?.addressSelected?.streetAddress,
                      city: state.userEdited?.addressSelected?.city,
                      postcode: state.userEdited?.addressSelected?.postCode,
                      fullAddress: state.userEdited?.addressSelected?.address,
                    },
                    carrier: state.drawdownConfig?.carrier,
                    dairySupplyNumber: state.userEdited?.dairySupplyNumber,
                    notes: state.userEdited?.deliveryInstructions,
                    truck: state.drawdownConfig?.truck,
                    freightType: state.drawdownConfig?.freightType,
                    contactName: state.drawdownConfig?.deliveryContactName,
                    contactPhone: state.drawdownConfig?.deliveryContactPhone,
                  }
                : null,
              contact: {
                name: state.contactDetails?.name,
                email: state.contactDetails?.email,
                phone: state.contactDetails?.phone,
                customerReference: state.contactDetails?.reference,
              },
              splitPayment: state.drawdownConfig?.splitPayment
                ? {
                    accountNumber: state.drawdownConfig?.splitPayment?.accountNumber,
                    accountName: state.drawdownConfig?.splitPayment?.accountName,
                    percentage: parseInt(state.drawdownConfig?.splitPayment?.percentage ?? '0'),
                  }
                : null,
              lines: state.productRequest.map((product) => ({
                itemNumber: product.productVariantNumber.split(':')[0].trim(),
                size: product.size,
                colour: product.colour,
                quantity: product.requestQuantity,
                feedRate: product.feedRate ? Number(product.feedRate) : undefined,
                additives: product.additives,
                unitLabel: capitaliseFirstLetter(
                  pluraliseUnit(product.unit, product.requestQuantity),
                ),
              })),
            };

            setLoading(true);
            submitDrawdown(payload, salesAgreement!.id)
              .then(() =>
                navigate(`/agreement/${salesAgreement?.id}/drawdown`, {
                  state: payload,
                }),
              )
              .catch((error) => {
                setSubmitError(error.message);
              })
              .finally(() => setLoading(false));
          }}
        >
          Submit
        </Button>
      </ModalActions>
    </div>
  );
};

export default Step4;
